/**
 * Weekly Pregnancy Symptoms Data
 * 
 * Proactive week-by-week guide showing what symptoms to expect during pregnancy.
 * 
 * SOURCES & REFERENCES:
 * - American College of Obstetricians and Gynecologists (ACOG)
 * - American Pregnancy Association (americanpregnancy.org)
 * - March of Dimes (marchofdimes.org)
 * - Mayo Clinic (mayoclinic.org)
 * 
 * Disclaimer: This content is for informational purposes only and does not
 * constitute medical advice. Always consult your healthcare provider.
 */

export interface WeeklySymptom {
    name: string;
    emoji: string;
    likelihood: 'common' | 'possible' | 'rare';
    description: string;
    relief?: string;
}

export interface WeeklySymptomData {
    week: number;
    trimester: 1 | 2 | 3;
    symptoms: WeeklySymptom[];
    bodyChanges: string[];
    partnerTip?: string;
}

// Symptoms grouped by trimester with week-specific variations
export const WEEKLY_SYMPTOMS: WeeklySymptomData[] = [
    // FIRST TRIMESTER (Weeks 1-13)
    {
        week: 4,
        trimester: 1,
        symptoms: [
            { name: 'Fatigue', emoji: '😴', likelihood: 'common', description: 'Extreme tiredness as your body works hard to support the pregnancy', relief: 'Rest when possible, go to bed earlier' },
            { name: 'Breast Tenderness', emoji: '🩹', likelihood: 'common', description: 'Breasts may feel sore, swollen, or tingly', relief: 'Wear a supportive bra, even at night' },
            { name: 'Mild Cramping', emoji: '😖', likelihood: 'possible', description: 'Light cramping as the uterus begins to expand', relief: 'Rest and use a warm (not hot) compress' },
            { name: 'Spotting', emoji: '🩸', likelihood: 'possible', description: 'Light spotting can occur during implantation', relief: 'Usually normal, but notify your provider if heavy' },
        ],
        bodyChanges: ['Missed period', 'Hormones (hCG) rapidly increasing', 'Uterus beginning to prepare'],
        partnerTip: 'She may not look pregnant yet, but she\'s likely exhausted. Take over some chores without being asked.',
    },
    {
        week: 5,
        trimester: 1,
        symptoms: [
            { name: 'Nausea', emoji: '🤢', likelihood: 'common', description: 'Morning sickness can start now (despite the name, it can happen anytime)', relief: 'Eat small, frequent meals; try ginger or crackers before getting up' },
            { name: 'Fatigue', emoji: '😴', likelihood: 'common', description: 'Exhaustion continues or intensifies', relief: 'Nap when you can, stay hydrated' },
            { name: 'Food Aversions', emoji: '🙅', likelihood: 'common', description: 'Certain foods or smells may become unbearable', relief: 'Avoid triggers, eat what sounds good' },
            { name: 'Frequent Urination', emoji: '🚽', likelihood: 'possible', description: 'Hormones increase blood flow to kidneys', relief: 'Don\'t reduce water intake; just plan bathroom access' },
        ],
        bodyChanges: ['Embryo is about the size of a sesame seed', 'Heart tube is forming', 'Hormone levels climbing'],
        partnerTip: 'Smells may trigger nausea—be mindful of cologne, cooking odors, and cleaning products.',
    },
    {
        week: 6,
        trimester: 1,
        symptoms: [
            { name: 'Morning Sickness', emoji: '🤢', likelihood: 'common', description: 'Nausea may be more pronounced, with or without vomiting', relief: 'Vitamin B6, ginger, small frequent meals' },
            { name: 'Mood Swings', emoji: '🎭', likelihood: 'common', description: 'Hormonal changes can cause emotional ups and downs', relief: 'Rest, communicate feelings, be patient with yourself' },
            { name: 'Bloating', emoji: '🎈', likelihood: 'common', description: 'Digestive slowdown causes bloating and gas', relief: 'Eat slowly, avoid carbonated drinks' },
            { name: 'Heightened Smell', emoji: '👃', likelihood: 'common', description: 'Sense of smell becomes super-powered', relief: 'Avoid strong odors, keep windows open' },
        ],
        bodyChanges: ['Baby\'s heart starts beating', 'Facial features beginning to form', 'Neural tube developing'],
        partnerTip: 'Mood swings are real and hormonal—don\'t take them personally. Just be supportive.',
    },
    {
        week: 7,
        trimester: 1,
        symptoms: [
            { name: 'Morning Sickness', emoji: '🤢', likelihood: 'common', description: 'Often peaks between weeks 7-9', relief: 'Eat before getting out of bed, stay hydrated' },
            { name: 'Fatigue', emoji: '😴', likelihood: 'common', description: 'Body is working overtime building the placenta', relief: 'Prioritize sleep, reduce commitments if possible' },
            { name: 'Acne', emoji: '😤', likelihood: 'possible', description: 'Hormonal changes can cause breakouts', relief: 'Gentle cleansing, avoid harsh products' },
            { name: 'Excess Saliva', emoji: '💧', likelihood: 'possible', description: 'Some women experience increased saliva production', relief: 'Chew gum, suck on hard candy' },
        ],
        bodyChanges: ['Baby is about the size of a blueberry', 'Arms and legs starting to form', 'Brain growing rapidly'],
        partnerTip: 'This is often when morning sickness is worst. Keep crackers by the bed and offer to make bland foods.',
    },
    {
        week: 8,
        trimester: 1,
        symptoms: [
            { name: 'Nausea', emoji: '🤢', likelihood: 'common', description: 'Morning sickness may be at its peak', relief: 'Eat protein-rich snacks, try acupressure wristbands' },
            { name: 'Constipation', emoji: '😣', likelihood: 'common', description: 'Progesterone slows digestion', relief: 'Fiber, water, gentle exercise like walking' },
            { name: 'Vivid Dreams', emoji: '💭', likelihood: 'possible', description: 'Hormones and anxiety can cause intense dreams', relief: 'Normal! Try relaxation before bed' },
            { name: 'Headaches', emoji: '🤕', likelihood: 'possible', description: 'Hormonal changes and dehydration can trigger headaches', relief: 'Stay hydrated, rest, ask doctor about safe pain relief' },
        ],
        bodyChanges: ['Baby is about the size of a raspberry', 'Fingers and toes forming', 'First ultrasound often happens this week'],
        partnerTip: 'Offer to attend the first ultrasound—it\'s an emotional milestone to share.',
    },
    {
        week: 9,
        trimester: 1,
        symptoms: [
            { name: 'Nausea', emoji: '🤢', likelihood: 'common', description: 'Still prevalent but may start to ease for some', relief: 'Keep eating small, frequent meals' },
            { name: 'Breast Changes', emoji: '🩹', likelihood: 'common', description: 'Breasts may feel fuller, veins more visible', relief: 'Supportive bra, moisturize if itchy' },
            { name: 'Fatigue', emoji: '😴', likelihood: 'common', description: 'Your body is growing a human—exhaustion is normal', relief: 'Rest without guilt' },
            { name: 'Heartburn', emoji: '🔥', likelihood: 'possible', description: 'Hormones relax the valve between stomach and esophagus', relief: 'Avoid spicy foods, don\'t lie down after eating' },
        ],
        bodyChanges: ['Baby now officially called a fetus', 'All essential organs have begun forming', 'Baby can hiccup!'],
        partnerTip: 'She may be irritable from constant nausea. Patience and small gestures of kindness go a long way.',
    },
    {
        week: 10,
        trimester: 1,
        symptoms: [
            { name: 'Nausea', emoji: '🤢', likelihood: 'common', description: 'May be improving for some, still strong for others', relief: 'Continue what\'s been working' },
            { name: 'Round Ligament Pain', emoji: '😖', likelihood: 'possible', description: 'Sharp pains on sides of belly as uterus grows', relief: 'Move slowly when changing positions' },
            { name: 'Visible Veins', emoji: '💙', likelihood: 'possible', description: 'Blood volume increasing means more visible veins', relief: 'Normal and temporary' },
            { name: 'Increased Vaginal Discharge', emoji: '💧', likelihood: 'common', description: 'More discharge is normal as cervix produces mucus', relief: 'Wear a panty liner; see doctor if odor or color changes' },
        ],
        bodyChanges: ['Baby is about the size of a strawberry', 'Bones are starting to harden', 'Baby can swallow and kick'],
        partnerTip: 'Grocery shopping with her can be tough if smells trigger nausea. Offer to go alone with a list.',
    },
    {
        week: 11,
        trimester: 1,
        symptoms: [
            { name: 'Nausea', emoji: '🤢', likelihood: 'common', description: 'Often the worst is behind you after this week', relief: 'Hang in there—relief is coming!' },
            { name: 'Fatigue', emoji: '😴', likelihood: 'common', description: 'Still tireder than usual but may improve soon', relief: 'Power naps can help' },
            { name: 'Leg Cramps', emoji: '🦵', likelihood: 'possible', description: 'Muscles may cramp, especially at night', relief: 'Stretch before bed, stay hydrated, eat bananas' },
            { name: 'Dizziness', emoji: '💫', likelihood: 'possible', description: 'Blood pressure changes can cause lightheadedness', relief: 'Stand up slowly, eat regularly' },
        ],
        bodyChanges: ['Baby is about the size of a fig', 'Fingers and toes are no longer webbed', 'Baby can open and close hands'],
        partnerTip: 'Energy may be returning soon—plan a low-key date or activity she enjoys.',
    },
    {
        week: 12,
        trimester: 1,
        symptoms: [
            { name: 'Reduced Nausea', emoji: '😊', likelihood: 'common', description: 'Many women feel nausea starting to lift', relief: 'Celebrate small wins, keep healthy snacks handy' },
            { name: 'Increased Energy', emoji: '⚡', likelihood: 'possible', description: 'Energy often begins returning', relief: 'Enjoy it! But don\'t overdo it' },
            { name: 'Headaches', emoji: '🤕', likelihood: 'possible', description: 'Hormonal fluctuations can still trigger headaches', relief: 'Rest, hydration, cool compress' },
            { name: 'Skin Changes', emoji: '✨', likelihood: 'possible', description: 'Some get the "pregnancy glow," others get acne', relief: 'Gentle skincare, stay hydrated' },
        ],
        bodyChanges: ['Baby is about the size of a lime', 'Nuchal translucency scan usually done now', 'Reflexes are developing'],
        partnerTip: 'NT scan can be stressful—plan something relaxing for after the appointment.',
    },
    {
        week: 13,
        trimester: 1,
        symptoms: [
            { name: 'More Energy', emoji: '⚡', likelihood: 'common', description: 'The golden second trimester is starting!', relief: 'Use this energy wisely—don\'t overcommit' },
            { name: 'Decreased Nausea', emoji: '😌', likelihood: 'common', description: 'Morning sickness usually fades significantly', relief: 'Maintain healthy eating habits' },
            { name: 'Growing Belly', emoji: '🤰', likelihood: 'possible', description: 'Bump may start to show', relief: 'Time for stretchy pants!' },
            { name: 'Food Cravings', emoji: '🍕', likelihood: 'common', description: 'Appetite often returns with specific cravings', relief: 'Enjoy in moderation' },
        ],
        bodyChanges: ['Baby is about the size of a peach', 'Fingerprints are forming', 'Vocal cords developing'],
        partnerTip: 'She may want to start sharing the news—discuss who to tell and when together.',
    },

    // SECOND TRIMESTER (Weeks 14-27)
    {
        week: 14,
        trimester: 2,
        symptoms: [
            { name: 'Increased Energy', emoji: '⚡', likelihood: 'common', description: 'Welcome to the "honeymoon phase" of pregnancy!', relief: 'Enjoy it—this is often the best trimester' },
            { name: 'Increased Appetite', emoji: '🍽️', likelihood: 'common', description: 'Hunger returns as nausea fades', relief: 'Choose nutritious foods, eat regularly' },
            { name: 'Nasal Congestion', emoji: '🤧', likelihood: 'possible', description: 'Increased blood flow can cause stuffiness', relief: 'Saline spray, humidifier' },
            { name: 'Round Ligament Pain', emoji: '😖', likelihood: 'possible', description: 'Sharp pains as ligaments stretch', relief: 'Move slowly, try gentle stretching' },
        ],
        bodyChanges: ['Baby is about the size of a lemon', 'Baby can squint and frown', 'Second trimester officially begins'],
        partnerTip: 'Great time to plan a babymoon or special outing while she has more energy.',
    },
    {
        week: 15,
        trimester: 2,
        symptoms: [
            { name: 'Sensitive Gums', emoji: '🦷', likelihood: 'possible', description: 'Gums may bleed more easily when brushing', relief: 'Use soft toothbrush, continue dental care' },
            { name: 'Forgetfulness', emoji: '🧠', likelihood: 'common', description: '"Pregnancy brain" is real!', relief: 'Write things down, set reminders' },
            { name: 'Stuffy Nose', emoji: '🤧', likelihood: 'possible', description: 'Hormones can cause chronic congestion', relief: 'Saline spray, sleep elevated' },
            { name: 'Nosebleeds', emoji: '🩸', likelihood: 'possible', description: 'Increased blood volume can cause nosebleeds', relief: 'Use a humidifier, apply gentle pressure if bleeding' },
        ],
        bodyChanges: ['Baby is about the size of an apple', 'Baby can sense light', 'Legs are longer than arms now'],
        partnerTip: 'Help her remember appointments and important dates—pregnancy brain is real!',
    },
    {
        week: 16,
        trimester: 2,
        symptoms: [
            { name: 'Back Pain', emoji: '🔙', likelihood: 'possible', description: 'Center of gravity shifting as belly grows', relief: 'Good posture, supportive shoes, prenatal yoga' },
            { name: 'Glowing Skin', emoji: '✨', likelihood: 'possible', description: 'Increased blood flow can create a healthy glow', relief: 'Enjoy it!' },
            { name: 'Constipation', emoji: '😣', likelihood: 'common', description: 'Hormones continue to slow digestion', relief: 'Fiber, water, gentle movement' },
            { name: 'Bigger Breasts', emoji: '👗', likelihood: 'common', description: 'Breasts continue to grow', relief: 'Get properly fitted for new bras' },
        ],
        bodyChanges: ['Baby is about the size of an avocado', 'Baby can hear sounds', 'Toenails are forming'],
        partnerTip: 'Offer a back rub—back pain is starting to become a regular companion.',
    },
    {
        week: 17,
        trimester: 2,
        symptoms: [
            { name: 'Increased Appetite', emoji: '🍽️', likelihood: 'common', description: 'Baby is growing fast and needs fuel!', relief: 'Healthy snacks, protein-rich foods' },
            { name: 'Stretch Marks', emoji: '🌊', likelihood: 'possible', description: 'May start appearing on belly, breasts, or thighs', relief: 'Moisturize, stay hydrated (they\'ll fade postpartum)' },
            { name: 'Dry Eyes', emoji: '👁️', likelihood: 'possible', description: 'Contact lenses may feel uncomfortable', relief: 'Lubricating eye drops, wear glasses more often' },
            { name: 'Dizziness', emoji: '💫', likelihood: 'possible', description: 'Blood pressure fluctuations continue', relief: 'Move slowly, don\'t skip meals' },
        ],
        bodyChanges: ['Baby is about the size of a pear', 'Fat is starting to form', 'Baby can move all joints'],
        partnerTip: 'She may be self-conscious about body changes—reassure her she\'s beautiful.',
    },
    {
        week: 18,
        trimester: 2,
        symptoms: [
            { name: 'Baby Movement', emoji: '🦋', likelihood: 'possible', description: 'You might start feeling flutters (quickening)!', relief: 'Lie still to notice subtle movements' },
            { name: 'Increased Hunger', emoji: '🍕', likelihood: 'common', description: 'Growing baby means bigger appetite', relief: 'Eat every 3-4 hours, choose balanced meals' },
            { name: 'Leg Cramps', emoji: '🦵', likelihood: 'possible', description: 'Often strike at night', relief: 'Stretch calves before bed, stay hydrated' },
            { name: 'Swelling', emoji: '💧', likelihood: 'possible', description: 'Mild swelling in feet and ankles may begin', relief: 'Elevate feet, reduce sodium, stay hydrated' },
        ],
        bodyChanges: ['Baby is about the size of a bell pepper', 'Baby can yawn and hiccup', 'Ears are in final position'],
        partnerTip: 'Ask to feel the baby move—even if you can\'t feel it yet, she\'ll appreciate you trying.',
    },
    {
        week: 19,
        trimester: 2,
        symptoms: [
            { name: 'Hip Pain', emoji: '🦴', likelihood: 'possible', description: 'Relaxin hormone loosens joints', relief: 'Sleep with pillow between knees, prenatal yoga' },
            { name: 'Skin Changes', emoji: '🌑', likelihood: 'possible', description: 'Linea nigra (dark line on belly) may appear', relief: 'Normal! Will fade after pregnancy' },
            { name: 'Baby Movement', emoji: '🦋', likelihood: 'common', description: 'Flutters becoming more noticeable', relief: 'Track patterns, enjoy this special feeling' },
            { name: 'Increased Discharge', emoji: '💧', likelihood: 'common', description: 'More vaginal discharge is normal', relief: 'Panty liners; see doctor if unusual color/odor' },
        ],
        bodyChanges: ['Baby is about the size of a mango', 'Vernix (protective coating) forming', 'Brain designating areas for senses'],
        partnerTip: 'Anatomy scan is usually around week 20—plan to join for this detailed look at baby!',
    },
    {
        week: 20,
        trimester: 2,
        symptoms: [
            { name: 'Shortness of Breath', emoji: '😮‍💨', likelihood: 'possible', description: 'Uterus is pushing up on diaphragm', relief: 'Slow down, practice good posture' },
            { name: 'Baby Kicks', emoji: '👶', likelihood: 'common', description: 'Movements are more defined and regular', relief: 'Enjoy! Start noticing patterns' },
            { name: 'Heartburn', emoji: '🔥', likelihood: 'common', description: 'Growing uterus pushes on stomach', relief: 'Small meals, avoid lying down after eating' },
            { name: 'Swollen Gums', emoji: '🦷', likelihood: 'possible', description: 'Pregnancy gingivitis is common', relief: 'Gentle brushing, flossing, dental checkup' },
        ],
        bodyChanges: ['Baby is about the size of a banana', 'Halfway point!', 'Anatomy scan reveals detailed development'],
        partnerTip: 'Anatomy scan day—be there to see baby\'s heart, brain, and maybe find out the sex!',
    },
    {
        week: 21,
        trimester: 2,
        symptoms: [
            { name: 'Strong Baby Kicks', emoji: '⚽', likelihood: 'common', description: 'Partner can often feel kicks now too!', relief: 'Bond with baby through belly' },
            { name: 'Backache', emoji: '🔙', likelihood: 'common', description: 'Weight gain shifts center of gravity', relief: 'Prenatal yoga, supportive shoes, good posture' },
            { name: 'Braxton Hicks', emoji: '😯', likelihood: 'possible', description: 'Practice contractions may begin', relief: 'Change positions, drink water, rest' },
            { name: 'Varicose Veins', emoji: '🦵', likelihood: 'possible', description: 'Increased blood volume affects veins', relief: 'Compression socks, elevate legs, exercise' },
        ],
        bodyChanges: ['Baby is about the size of a carrot', 'Baby can taste amniotic fluid', 'Eyebrows and eyelids formed'],
        partnerTip: 'You can probably feel kicks now—put your hand on her belly!',
    },
    {
        week: 22,
        trimester: 2,
        symptoms: [
            { name: 'Stretch Marks', emoji: '🌊', likelihood: 'common', description: 'More visible as belly rapidly grows', relief: 'Moisturize, embrace them as tiger stripes!' },
            { name: 'Hemorrhoids', emoji: '😬', likelihood: 'possible', description: 'Pressure on veins can cause hemorrhoids', relief: 'Fiber, hydration, sitz baths' },
            { name: 'Increased Libido', emoji: '❤️', likelihood: 'possible', description: 'Many women experience increased desire', relief: 'Enjoy! Sex during pregnancy is safe for most' },
            { name: 'Ankle Swelling', emoji: '🦶', likelihood: 'possible', description: 'Mild edema is normal', relief: 'Elevate feet, reduce salt, stay hydrated' },
        ],
        bodyChanges: ['Baby is about the size of a papaya', 'Baby has a sense of touch', 'Lungs are developing'],
        partnerTip: 'Physical intimacy may increase—communicate openly about what feels comfortable.',
    },
    {
        week: 23,
        trimester: 2,
        symptoms: [
            { name: 'Linea Nigra', emoji: '➖', likelihood: 'common', description: 'Dark line down the belly is prominent', relief: 'Will fade after delivery' },
            { name: 'Swollen Ankles', emoji: '🦶', likelihood: 'common', description: 'Fluid retention increases', relief: 'Elevate legs, avoid standing for long periods' },
            { name: 'Difficulty Sleeping', emoji: '🌙', likelihood: 'possible', description: 'Hard to find comfortable positions', relief: 'Pregnancy pillow, sleep on left side' },
            { name: 'Carpal Tunnel', emoji: '✋', likelihood: 'possible', description: 'Swelling can compress nerves in wrists', relief: 'Wrist braces, shake out hands frequently' },
        ],
        bodyChanges: ['Baby is about the size of a grapefruit', 'Baby responds to sounds', 'Practicing breathing movements'],
        partnerTip: 'Sleep is getting harder—consider getting her a pregnancy pillow as a gift.',
    },
    {
        week: 24,
        trimester: 2,
        symptoms: [
            { name: 'Itchy Belly', emoji: '🙌', likelihood: 'common', description: 'Skin stretching causes itchiness', relief: 'Moisturize frequently, avoid hot showers' },
            { name: 'Back Pain', emoji: '🔙', likelihood: 'common', description: 'Belly weight pulls on lower back', relief: 'Prenatal massage, warm baths, good posture' },
            { name: 'Dry Eyes', emoji: '👁️', likelihood: 'possible', description: 'Hormones affect tear production', relief: 'Artificial tears, less screen time' },
            { name: 'Blurry Vision', emoji: '👓', likelihood: 'possible', description: 'Temporary vision changes from fluid retention', relief: 'Usually temporary; mention to doctor' },
        ],
        bodyChanges: ['Baby is about the size of an ear of corn', 'Glucose screening test typically done', 'Baby\'s face is fully formed'],
        partnerTip: 'Glucose test day can be uncomfortable—bring her a protein snack for afterward.',
    },
    {
        week: 25,
        trimester: 2,
        symptoms: [
            { name: 'Restless Legs', emoji: '🦵', likelihood: 'possible', description: 'Uncomfortable urge to move legs, especially at night', relief: 'Stretch, walk, ask doctor about iron/magnesium' },
            { name: 'Snoring', emoji: '😴', likelihood: 'possible', description: 'Swelling in nasal passages causes snoring', relief: 'Sleep elevated, nasal strips' },
            { name: 'Frequent Urination', emoji: '🚽', likelihood: 'common', description: 'Baby pressing on bladder more', relief: 'Keep drinking water, plan bathroom access' },
            { name: 'Clumsiness', emoji: '🙈', likelihood: 'possible', description: 'Shifting balance plus pregnancy brain', relief: 'Move carefully, hold handrails' },
        ],
        bodyChanges: ['Baby is about the size of a cauliflower', 'Hair starting to grow', 'Baby responds to your voice'],
        partnerTip: 'Talk and sing to baby—studies show they recognize voices heard in the womb!',
    },
    {
        week: 26,
        trimester: 2,
        symptoms: [
            { name: 'Braxton Hicks', emoji: '😯', likelihood: 'common', description: 'Practice contractions becoming more noticeable', relief: 'Change positions, hydrate, rest' },
            { name: 'Difficulty Sleeping', emoji: '🌙', likelihood: 'common', description: 'Getting comfortable is increasingly hard', relief: 'Pregnancy pillow, side sleeping with support' },
            { name: 'Lower Back Pain', emoji: '🔙', likelihood: 'common', description: 'Weight and posture changes strain back', relief: 'Prenatal yoga, massage, warm compress' },
            { name: 'Bloating', emoji: '🎈', likelihood: 'possible', description: 'Digestion continues to slow', relief: 'Small frequent meals, avoid gas-producing foods' },
        ],
        bodyChanges: ['Baby is about the size of a zucchini', 'Eyes begin to open', 'Baby can see light through belly'],
        partnerTip: 'Her sleep quality is decreasing—let her sleep in on weekends if possible.',
    },
    {
        week: 27,
        trimester: 2,
        symptoms: [
            { name: 'Leg Cramps', emoji: '🦵', likelihood: 'common', description: 'Nighttime leg cramps may wake her', relief: 'Flex feet (not point), stretch calves before bed' },
            { name: 'Pelvic Pressure', emoji: '⬇️', likelihood: 'possible', description: 'Baby settling lower creates pressure', relief: 'Pelvic tilts, rest when needed' },
            { name: 'Heartburn', emoji: '🔥', likelihood: 'common', description: 'Gets worse as baby takes up more room', relief: 'Prop up when sleeping, avoid trigger foods' },
            { name: 'Swelling', emoji: '💧', likelihood: 'common', description: 'Feet, ankles, and hands may swell more', relief: 'Elevate, stay cool, compression socks' },
        ],
        bodyChanges: ['Baby is about the size of a head of lettuce', 'Third trimester begins next week!', 'Baby can dream (REM sleep detected)'],
        partnerTip: 'Second trimester ends soon—consider a final before-baby date night.',
    },

    // THIRD TRIMESTER (Weeks 28-40)
    {
        week: 28,
        trimester: 3,
        symptoms: [
            { name: 'Shortness of Breath', emoji: '😮‍💨', likelihood: 'common', description: 'Baby pushing up on diaphragm', relief: 'Sit upright, take breaks, don\'t overexert' },
            { name: 'Trouble Sleeping', emoji: '🌙', likelihood: 'common', description: 'Finding comfortable positions is tough', relief: 'Pillow fortress, left side sleeping, limit fluids before bed' },
            { name: 'Braxton Hicks', emoji: '😯', likelihood: 'common', description: 'Practice contractions more frequent', relief: 'If more than 4 in an hour, call provider' },
            { name: 'Leaky Breasts', emoji: '🍼', likelihood: 'possible', description: 'Colostrum (early milk) may leak', relief: 'Nursing pads in bra' },
        ],
        bodyChanges: ['Baby is about the size of an eggplant', 'Third trimester officially begins!', 'Baby is developing fat layers'],
        partnerTip: 'Kick counts start now—baby should move 10 times within 2 hours. Help her track.',
    },
    {
        week: 29,
        trimester: 3,
        symptoms: [
            { name: 'Fatigue Returns', emoji: '😴', likelihood: 'common', description: 'Third trimester exhaustion sets in', relief: 'Rest when possible, delegate tasks' },
            { name: 'Varicose Veins', emoji: '🦵', likelihood: 'possible', description: 'May appear or worsen', relief: 'Compression stockings, elevate legs' },
            { name: 'Hemorrhoids', emoji: '😬', likelihood: 'possible', description: 'Pressure from baby can worsen them', relief: 'Fiber, hydration, sitz baths, witch hazel pads' },
            { name: 'Constipation', emoji: '😣', likelihood: 'common', description: 'Very common in third trimester', relief: 'Fiber, water, gentle walks' },
        ],
        bodyChanges: ['Baby is about the size of a butternut squash', 'Baby can blink', 'Brain is growing rapidly'],
        partnerTip: 'Third trimester fatigue is real—take over more household duties.',
    },
    {
        week: 30,
        trimester: 3,
        symptoms: [
            { name: 'Frequent Urination', emoji: '🚽', likelihood: 'common', description: 'Baby pressing on bladder constantly', relief: 'Lean forward when peeing to fully empty' },
            { name: 'Heartburn', emoji: '🔥', likelihood: 'common', description: 'Severe heartburn is common now', relief: 'Tums, small meals, sleep propped up' },
            { name: 'Pelvic Pain', emoji: '⬇️', likelihood: 'possible', description: 'Symphysis pubis dysfunction (SPD) pain', relief: 'Avoid wide leg movements, physio exercises' },
            { name: 'Mood Swings', emoji: '🎭', likelihood: 'possible', description: 'Hormones plus anxiety about birth', relief: 'Talk about fears, consider birth class' },
        ],
        bodyChanges: ['Baby is about the size of a cabbage', 'Baby\'s skin is smoothing out', 'Almost 3 pounds now'],
        partnerTip: 'She may be anxious about birth—watch childbirth education videos together.',
    },
    {
        week: 31,
        trimester: 3,
        symptoms: [
            { name: 'Aches & Pains', emoji: '😖', likelihood: 'common', description: 'Body is under significant strain', relief: 'Prenatal massage, warm baths, rest' },
            { name: 'Waddling', emoji: '🚶', likelihood: 'common', description: 'Walking becomes a waddle due to pelvic changes', relief: 'This is normal! Take your time' },
            { name: 'Forgetfulness', emoji: '🧠', likelihood: 'common', description: 'Pregnancy brain continues', relief: 'Lists, reminders, don\'t be hard on yourself' },
            { name: 'Insomnia', emoji: '🌙', likelihood: 'common', description: 'Between bathroom trips and discomfort', relief: 'Relaxation techniques, consistent bedtime routine' },
        ],
        bodyChanges: ['Baby is about the size of a coconut', 'Baby\'s five senses are fully developed', 'Head is getting larger for brain growth'],
        partnerTip: 'Start assembling baby gear—crib, changing table, car seat.',
    },
    {
        week: 32,
        trimester: 3,
        symptoms: [
            { name: 'Braxton Hicks', emoji: '😯', likelihood: 'common', description: 'More frequent and stronger', relief: 'Stay hydrated, change positions' },
            { name: 'Shortness of Breath', emoji: '😮‍💨', likelihood: 'common', description: 'Limited lung space is noticeable', relief: 'Sit straight, don\'t rush movements' },
            { name: 'Leaky Breasts', emoji: '🍼', likelihood: 'common', description: 'Colostrum production increases', relief: 'Nursing pads, it\'s a good sign!' },
            { name: 'Difficulty Bending', emoji: '🙌', likelihood: 'common', description: 'Big belly makes bending over hard', relief: 'Ask for help, use long-handled tools' },
        ],
        bodyChanges: ['Baby is about the size of a squash', 'Baby shower time!', 'Baby is about 4 pounds'],
        partnerTip: 'Help with anything requiring bending—shoes, picking things up off the floor.',
    },
    {
        week: 33,
        trimester: 3,
        symptoms: [
            { name: 'Hip & Pelvic Pain', emoji: '🦴', likelihood: 'common', description: 'Joints loosening for birth', relief: 'Pillow between knees, physio exercises' },
            { name: 'Overheating', emoji: '🥵', likelihood: 'possible', description: 'Increased metabolism raises body temp', relief: 'Dress in layers, stay hydrated' },
            { name: 'Headaches', emoji: '🤕', likelihood: 'possible', description: 'Hormones, fatigue, tension all contribute', relief: 'Stay hydrated, rest, ask doctor about safe pain relief' },
            { name: 'Clumsiness', emoji: '🙈', likelihood: 'common', description: 'Center of gravity is way off', relief: 'Move slowly and carefully' },
        ],
        bodyChanges: ['Baby is about the size of a pineapple', 'Baby\'s bones are hardening (except skull for delivery)', 'Immune system developing'],
        partnerTip: 'She may be hot when you\'re comfortable—let her control the thermostat!',
    },
    {
        week: 34,
        trimester: 3,
        symptoms: [
            { name: 'Fatigue', emoji: '😴', likelihood: 'common', description: 'Carrying significant extra weight', relief: 'Naps, early bedtime, accept help' },
            { name: 'Blurry Vision', emoji: '👓', likelihood: 'possible', description: 'Fluid changes can affect vision', relief: 'Usually temporary; mention to doctor' },
            { name: 'Swollen Feet', emoji: '🦶', likelihood: 'common', description: 'Edema worsens in late pregnancy', relief: 'Elevate, cool water soaks, avoid sodium' },
            { name: 'Itchy Belly', emoji: '🙌', likelihood: 'common', description: 'Skin stretched to max', relief: 'Moisturize generously, oatmeal baths' },
        ],
        bodyChanges: ['Baby is about the size of a cantaloupe', 'Baby may drop into pelvis (lightening)', 'Central nervous system maturing'],
        partnerTip: 'Her shoes may not fit—consider slip-on shoes or sandals as a gift.',
    },
    {
        week: 35,
        trimester: 3,
        symptoms: [
            { name: 'Frequent Urination', emoji: '🚽', likelihood: 'common', description: 'Baby\'s head pressing directly on bladder', relief: 'Stay near bathrooms, limit evening fluids' },
            { name: 'Pelvic Pressure', emoji: '⬇️', likelihood: 'common', description: 'Feels like baby could fall out', relief: 'Normal "lightening," rest when needed' },
            { name: 'Difficulty Breathing', emoji: '😮‍💨', likelihood: 'common', description: 'May ease if baby drops', relief: 'If baby drops, breathing gets easier!' },
            { name: 'Waddling', emoji: '🚶', likelihood: 'common', description: 'Very pronounced now', relief: 'Embrace the waddle!' },
        ],
        bodyChanges: ['Baby is about the size of a honeydew melon', 'Baby is gaining about half a pound per week', 'Most organs fully developed'],
        partnerTip: 'Group B Strep test is soon—it\'s a quick, painless swab.',
    },
    {
        week: 36,
        trimester: 3,
        symptoms: [
            { name: 'Braxton Hicks', emoji: '😯', likelihood: 'common', description: 'May be stronger and more frequent', relief: 'Time them—if regular, could be real labor!' },
            { name: 'Pelvic Pain', emoji: '⬇️', likelihood: 'common', description: 'Baby\'s head is very low', relief: 'Rest, warm baths, pelvic floor exercises' },
            { name: 'Nesting Urge', emoji: '🏠', likelihood: 'common', description: 'Strong urge to clean and organize', relief: 'Channel it productively, but don\'t overdo it' },
            { name: 'Trouble Sleeping', emoji: '🌙', likelihood: 'common', description: 'Hard to get comfortable', relief: 'Multiple pillows, naps during the day' },
        ],
        bodyChanges: ['Baby is about the size of a head of romaine lettuce', 'GBS test completed', 'Baby is considered early term at week\'s end'],
        partnerTip: 'Nesting is real—help with projects but don\'t let her overexert.',
    },
    {
        week: 37,
        trimester: 3,
        symptoms: [
            { name: 'Lightning Crotch', emoji: '⚡', likelihood: 'possible', description: 'Sharp shooting pains in pelvis/vagina', relief: 'Normal! Baby is pressing on nerves' },
            { name: 'Easier Breathing', emoji: '😌', likelihood: 'possible', description: 'If baby has dropped, lungs have more room', relief: 'Enjoy breathing easier!' },
            { name: 'Increased Discharge', emoji: '💧', likelihood: 'common', description: 'Mucus plug may start to come out', relief: 'Totally normal—labor could be weeks away still' },
            { name: 'Anxiety', emoji: '😰', likelihood: 'common', description: 'Birth is imminent', relief: 'Review birth plan, practice breathing' },
        ],
        bodyChanges: ['Baby is considered full-term!', 'Baby is about 6.5 pounds', 'Hospital bag should be packed'],
        partnerTip: 'Hospital bag should be packed and in the car. Know the route to hospital!',
    },
    {
        week: 38,
        trimester: 3,
        symptoms: [
            { name: 'Heavy Feeling', emoji: '⬇️', likelihood: 'common', description: 'Baby feels very heavy and low', relief: 'Rest frequently, baby could come any day' },
            { name: 'Contractions', emoji: '⏱️', likelihood: 'possible', description: 'May be hard to tell Braxton Hicks from real', relief: 'Time them: consistent pattern = call doctor' },
            { name: 'Nausea', emoji: '🤢', likelihood: 'possible', description: 'May return before labor begins', relief: 'Small meals, stay hydrated' },
            { name: 'Diarrhea', emoji: '💩', likelihood: 'possible', description: 'Body clearing out before labor', relief: 'Often a sign labor is approaching' },
        ],
        bodyChanges: ['Baby is about the size of a winter melon', 'All organ systems mature and ready', 'Baby continues to add fat'],
        partnerTip: 'Know the signs of labor: regular contractions, water breaking, bloody show.',
    },
    {
        week: 39,
        trimester: 3,
        symptoms: [
            { name: 'Extreme Fatigue', emoji: '😴', likelihood: 'common', description: 'Your body is ready to give birth', relief: 'Rest as much as possible' },
            { name: 'Burst of Energy', emoji: '⚡', likelihood: 'possible', description: 'Nesting energy surge before labor', relief: 'Use it wisely, don\'t exhaust yourself' },
            { name: 'Loose Bowels', emoji: '💩', likelihood: 'possible', description: 'Prostaglandins can cause diarrhea', relief: 'Stay hydrated, often means labor is near' },
            { name: 'Mucus Plug', emoji: '🔌', likelihood: 'possible', description: 'May lose mucus plug', relief: 'Labor could start soon—or in weeks' },
        ],
        bodyChanges: ['Baby is about the size of a watermelon', 'Baby is about 7-8 pounds on average', 'Any day now!'],
        partnerTip: 'Be on standby. Keep your phone charged and nearby at all times.',
    },
    {
        week: 40,
        trimester: 3,
        symptoms: [
            { name: 'Impatience', emoji: '😤', likelihood: 'common', description: 'Due date is here!', relief: 'Only 5% of babies arrive on their due date' },
            { name: 'Bloody Show', emoji: '🩸', likelihood: 'possible', description: 'Blood-tinged mucus indicates cervical changes', relief: 'Labor may be starting—call provider' },
            { name: 'Water Breaking', emoji: '💧', likelihood: 'possible', description: 'Could happen any time', relief: 'Note the time, color, and call provider' },
            { name: 'Contractions', emoji: '⏱️', likelihood: 'possible', description: 'The real deal may begin', relief: '5-1-1 rule: 5 min apart, 1 min long, for 1 hour = hospital' },
        ],
        bodyChanges: ['Baby is fully developed', 'Average weight 7.5 pounds', 'Due date—but baby comes when ready!'],
        partnerTip: 'Be patient but ready. Have the car seat installed and bags in the car.',
    },
];

/**
 * Get symptom data for a specific week
 */
export function getWeeklySymptoms(week: number): WeeklySymptomData | undefined {
    // Clamp week to valid range
    const clampedWeek = Math.max(4, Math.min(40, week));
    return WEEKLY_SYMPTOMS.find(w => w.week === clampedWeek);
}

/**
 * Get symptoms for current week with nearby weeks as context
 */
export function getSymptomContext(currentWeek: number): {
    current: WeeklySymptomData | undefined;
    upcoming: WeeklySymptomData[];
} {
    const current = getWeeklySymptoms(currentWeek);
    const upcoming = WEEKLY_SYMPTOMS
        .filter(w => w.week > currentWeek && w.week <= currentWeek + 4)
        .slice(0, 2);

    return { current, upcoming };
}
